import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { PrimaryButton } from "@shared/buttons";
import { toast } from "react-toastify";
import {
  requestResetPasswordOTP,
  requestSignupOTP,
} from "@services/krent.service";
import { formatTime } from "@utils/helpers";

interface OtpStepProps {
  email: string;
  onVerify: (otp: string) => void;
  onBack: () => void;
  loading?: boolean;
  type?: "signup" | "reset";
}

export const OtpStep: React.FC<OtpStepProps> = ({
  email,
  onVerify,
  onBack,
  loading,
  type = "signup",
}) => {
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(120);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    let countdown: NodeJS.Timeout;

    if (timer > 0) {
      countdown = setTimeout(() => setTimer(timer - 1), 1000);
    }

    return () => clearTimeout(countdown);
  }, [timer]);

  const handleResend = async () => {
    try {
      setResending(true);

      let res;
      if (type === "signup") {
        res = await requestSignupOTP(email);
      } else {
        res = await requestResetPasswordOTP(email);
      }

      toast.success(res.message || "OTP resent successfully");
      setTimer(30);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.errors?.[0]?.message ||
          err?.message ||
          "Failed to resend OTP",
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="px-4 py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <button
          onClick={onBack}
          className="btn btn-link text-decoration-none p-0"
          style={{ color: "#47BBA9" }}
        >
          ← Back
        </button>
      </div>

      <h4 className="fw-semibold fs-18 font-onest mb-2">
        Check your mail, we have sent you a code
      </h4>

      <div className="mb-4">
        <p>A 6-digit code has been sent to {email}.</p>
        <p>Enter it below to verify and continue.</p>
      </div>

      <input
        type="text"
        maxLength={6}
        className="form-control mb-3"
        placeholder="Enter OTP"
        value={otp}
        onChange={(e) => setOtp(e.target.value)}
      />

      <div className="d-flex gap-2 mb-4 mt-5">
        <PrimaryButton
          text={
            loading
              ? "Verifying..."
              : type === "reset"
                ? "Verify and continue"
                : "Proceed"
          }
          type="primary"
          click={() => onVerify(otp)}
          loading={loading}
          disable={loading}
        />
      </div>

      <div className="mb-4 text-center">
        {timer > 0 ? (
          <small className="text-muted text-primary fw-500 btn-link bg-white">
            Resend OTP in {formatTime(timer)}s
          </small>
        ) : (
          <button
            className="btn p-0 text-primary fw-500"
            style={{ fontSize: "14px" }}
            disabled={resending}
            onClick={handleResend}
          >
            {resending ? "Resending..." : "Resend OTP"}
          </button>
        )}
      </div>
    </div>
  );
};

interface OtpModalProps {
  email: string;
  open: boolean;
  onClose: () => void;
  onVerify: (otp: string) => void;
  loading?: boolean;
  type?: "signup" | "reset";
}

const OtpModal: React.FC<OtpModalProps> = ({
  email,
  open,
  onClose,
  onVerify,
  loading,
  type = "signup",
}) => {
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(120);
  const [resending, setResending] = useState(false);

  useEffect(() => {
    let countdown: NodeJS.Timeout;

    if (open && timer > 0) {
      countdown = setTimeout(() => setTimer(timer - 1), 1000);
    }

    return () => clearTimeout(countdown);
  }, [timer, open]);

  const handleResend = async () => {
    try {
      setResending(true);

      let res;
      if (type === "signup") {
        res = await requestSignupOTP(email);
      } else {
        res = await requestResetPasswordOTP(email);
      }

      toast.success(res.message || "OTP resent successfully");
      setTimer(30);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.errors?.[0]?.message ||
          err?.message ||
          "Failed to resend OTP",
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <Modal show={open} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Check your email, we have sent you a code</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="mb-3">
          Enter the OTP sent to <strong>{email}</strong>
        </p>

        <input
          type="text"
          maxLength={6}
          className="form-control"
          placeholder="Enter OTP"
          value={otp}
          onChange={(e) => setOtp(e.target.value)}
        />

        <div className="mt-3 text-center">
          {timer > 0 ? (
            <small className="text-muted">
              Resend OTP in {formatTime(timer)}s
            </small>
          ) : (
            <button
              className="btn btn-link p-0"
              style={{ fontSize: "14px" }}
              disabled={resending}
              onClick={handleResend}
            >
              {resending ? "Resending..." : "Resend OTP"}
            </button>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <PrimaryButton
          text={
            loading
              ? "Verifying..."
              : type === "reset"
                ? " Verify and continue"
                : "Verify and Signup"
          }
          type="primary"
          click={() => onVerify(otp)}
          loading={loading}
          disable={loading}
        />
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OtpModal;
